"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { createTagAction, updateTagAction, deleteTagAction } from "./_actions";

export type Tag = {
  id: string;
  slug: string;
  name: string;
  articleCount: number;
};

export function TagCreateForm() {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await createTagAction(fd);
      if (!res.ok) setError(res.error);
      else {
        (e.target as HTMLFormElement).reset();
        router.refresh();
      }
    });
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-stroke-subtle bg-bg-elevated/40 p-4 mb-6 flex flex-wrap items-end gap-2">
      {error && (
        <div className="w-full text-sm text-rose-300">{error}</div>
      )}
      <input
        name="name"
        required
        placeholder="Tên tag (vd: Bitcoin)"
        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm"
      />
      <input
        name="slug"
        placeholder="slug (tự sinh nếu trống)"
        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm font-mono"
      />
      <button
        type="submit"
        disabled={pending}
        className="px-5 py-2 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary/90 disabled:opacity-50 transition"
      >
        {pending ? "Đang tạo…" : "+ Thêm tag"}
      </button>
    </form>
  );
}

export function TagRow({ t }: { t: Tag }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await updateTagAction(t.id, fd);
      if (!res.ok) setError(res.error);
      else {
        setEditing(false);
        router.refresh();
      }
    });
  }

  function remove() {
    if (!confirm(`Xóa tag "${t.name}"? ${t.articleCount > 0 ? `(${t.articleCount} bài đang gắn tag này)` : ""}`)) return;
    startTransition(async () => {
      const res = await deleteTagAction(t.id);
      if (!res.ok) setError(res.error);
      else router.refresh();
    });
  }

  if (editing) {
    return (
      <tr className="border-b border-stroke-subtle/60">
        <td colSpan={4} className="p-3 bg-bg-elevated/30">
          <form onSubmit={save} className="flex flex-wrap items-center gap-2">
            {error && <div className="w-full text-sm text-rose-300">{error}</div>}
            <input name="name" defaultValue={t.name} required className="flex-1 min-w-[150px] px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm" />
            <input name="slug" defaultValue={t.slug} required className="flex-1 min-w-[150px] px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm font-mono" />
            <button type="submit" disabled={pending} className="px-3 py-1.5 rounded bg-accent-primary text-white text-sm disabled:opacity-50">{pending ? "…" : "Lưu"}</button>
            <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink-primary">Hủy</button>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-stroke-subtle/60 last:border-0 hover:bg-bg-elevated/30 transition">
      <td className="px-4 py-3 font-medium">{t.name}</td>
      <td className="px-4 py-3"><code className="text-xs px-1.5 py-0.5 rounded bg-bg-overlay font-mono">{t.slug}</code></td>
      <td className="px-4 py-3 text-sm text-ink-secondary">{t.articleCount}</td>
      <td className="px-4 py-3 text-right">
        {error && <div className="text-xs text-rose-300 mb-1">{error}</div>}
        <button type="button" onClick={() => setEditing(true)} className="text-xs text-accent-primary hover:underline mr-3">Sửa</button>
        <button type="button" onClick={remove} disabled={pending} className="text-xs text-rose-300 hover:underline disabled:opacity-50">Xóa</button>
      </td>
    </tr>
  );
}
